import { Field, FieldArray, FieldInputProps, FormikProps, getIn } from "formik";
import { Show, ShowDealType } from "@quantizr-engineering/common/types/show";
import megaphone from "../assets/megaphone.svg";
import userProfile01 from "../assets/userProfile01.svg";
import alignLeft from "../assets/alignleft.svg";
import file02 from "../assets/file02.svg";
import coinUnbroken from "../assets/coinunbroken.svg";
import shirt01 from "../assets/shirt01.svg";
import SelectCell from "./SelectCell";
import TextCell from "./TextCell";
import NumberCell from "./NumberCell";
import calendarNumber from "../assets/calendar-number.svg";
import DateCell from "./DateCell";
import building from "../assets/building.svg";

const dealTypeOptions = Object.values(ShowDealType).map((type) => ({
  value: type,
  label: type,
}));

const columns = [
  {
    icon: megaphone,
    label: "Promoters",
    fieldname: "promoter",
    type: "text",
  },
  {
    icon: building,
    label: "Venue",
    fieldname: "venue",
    type: "text",
  },
  {
    icon: building,
    label: "Location",
    fieldnames: ["city", "state"],
    type: "text",
  },
  {
    icon: userProfile01,
    label: "Appearing with",
    fieldname: "appearingWith",
    type: "text",
  },
  {
    icon: calendarNumber,
    label: "Performance Date",
    fieldname: "performanceDate",
    type: "date",
  },
  {
    icon: file02,
    label: "Deal type",
    fieldname: "dealType",
    type: "select",
    selectOptions: dealTypeOptions,
  },
  {
    icon: coinUnbroken,
    label: "Profit share",
    fieldname: "profitSharePercent",
    type: "number",
    isPercentage: true,
  },
  {
    icon: shirt01,
    label: "Merch hard/soft",
    fieldnames: ["merchHard", "merchSoft"],
    type: "number",
    isPercentage: true,
  },
  {
    icon: alignLeft,
    label: "Description",
    fieldname: "description",
    type: "text",
  },
];

interface InfoSectionProps {
  data: Show;
}

const InfoSection: React.FC<InfoSectionProps> = () => {
  const renderCellComponent = (
    column: any,
    field: FieldInputProps<any>,
    onBlur: (e: React.FocusEvent<any>) => void,
    hasError?: boolean
  ) => {
    switch (column.type) {
      case "text":
        return <TextCell {...field} onBlur={onBlur} />;
      case "number":
        return (
          <NumberCell
            {...field}
            onBlur={onBlur}
            isPercentage={column.isPercentage}
          />
        );
      case "select":
        return (
          <SelectCell
            {...field}
            onBlur={onBlur}
            options={column.selectOptions || []}
            hasError={hasError || false}
          />
        );
      case "date":
        return <DateCell {...field} onBlur={onBlur} />;
      default:
        return null;
    }
  };

  const renderSingleField = (column: any) => {
    if (!column.fieldname) return null;

    return (
      <Field name={column.fieldname}>
        {({
          field,
          form,
        }: {
          field: FieldInputProps<any>;
          form: FormikProps<any>;
        }) => {
          const error = getIn(form.errors, column.fieldname);
          const touched = getIn(form.touched, column.fieldname);
          const hasError = !!(error && touched);

          const onBlur = (e: React.FocusEvent<any>) => {
            field.onBlur(e);
            if (form.dirty) {
              form.handleSubmit(e);
            }
          };

          const component = renderCellComponent(
            column,
            field,
            onBlur,
            hasError
          );

          return (
            <div
              className={`flex-1 p-2 min-h-[60px] truncate whitespace-nowrap overflow-hidden relative ${
                hasError ? "border-2 border-red-500" : ""
              }`}
            >
              {component}
              <div className="text-xs text-red-500 mt-1">
                {column.type === "date" && error?.includes("Invalid Date")
                  ? "Date must be valid"
                  : error}
              </div>
            </div>
          );
        }}
      </Field>
    );
  };

  const renderMultipleFields = (column: any) => {
    if (!column.fieldnames) return null;

    return (
      <div className="flex gap-2 p-2">
        {column.fieldnames.map((fieldname: string) => (
          <Field key={fieldname} name={fieldname}>
            {({
              field,
              form,
            }: {
              field: FieldInputProps<any>;
              form: FormikProps<any>;
            }) => {
              const onBlur = (e: React.FocusEvent<any>) => {
                field.onBlur(e);
                if (form.dirty) {
                  form.handleSubmit(e);
                }
              };

              return renderCellComponent(column, field, onBlur);
            }}
          </Field>
        ))}
      </div>
    );
  };

  const renderColumnRow = (column: any, index: number) => (
    <div
      key={index}
      className="grid grid-cols-[1fr_2fr] gap-4 items-center h-9 min-h-[36px]"
    >
      <div className="flex gap-2 items-center">
        <span className="text-2xl">
          <img src={column.icon} alt={column.label} className="w-4 h-4" />
        </span>
        <span className="text-sm text-left">{column.label}</span>
      </div>
      <div className="flex min-w-0 overflow-hidden">
        {column.fieldnames
          ? renderMultipleFields(column)
          : renderSingleField(column)}
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-2 gap-8 p-4 h-full">
      <FieldArray name="infoSection">
        {() => (
          <>
            <div className="space-y-4">
              {columns
                .slice(0, 5)
                .map((column, index) => renderColumnRow(column, index))}
            </div>

            <div className="space-y-4">
              {columns
                .slice(5)
                .map((column, index) => renderColumnRow(column, index))}
            </div>
          </>
        )}
      </FieldArray>
    </div>
  );
};

export default InfoSection;
